import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { trackFacebookEvent } from '../utils/facebookPixel';
import './Auth.css';

// API Base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const Login: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError(''); // Clear error when user types
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(formData);

      // Track successful login as Lead event
      trackFacebookEvent('Lead', {
        content_name: 'User Login',
        content_category: 'Authentication'
      });

      // Get the updated auth context to check user role
      // Note: We need to wait a tick for the context to update
      setTimeout(() => {
        // Check if there's a redirect location from protected route
        const from = (location.state as any)?.from?.pathname;

        // If user was trying to access a specific page, redirect there
        if (from && from !== '/login') {
          navigate(from, { replace: true });
        } else {
          // Otherwise, redirect based on role
          // Check localStorage for the profile data that was just set
          const authTokens = localStorage.getItem('authTokens');
          if (authTokens) {
            // Fetch profile to determine role
            fetch(`${API_BASE_URL}/customer/profile/`, {
              headers: {
                'Authorization': `Bearer ${JSON.parse(authTokens).access}`
              }
            })
              .then(res => res.json())
              .then(profile => {
                if (profile.is_staff || profile.is_superuser) {
                  navigate('/admin/dashboard', { replace: true });
                } else {
                  navigate('/dashboard', { replace: true });
                }
              })
              .catch(() => {
                // Fallback to customer dashboard if profile fetch fails
                navigate('/dashboard', { replace: true });
              });
          } else {
            navigate('/dashboard', { replace: true });
          }
        }
      }, 100);
    } catch (err: any) {
      console.error('Login error:', err);
      let errorMessage = '';
      if (err.response?.status === 401) {
        errorMessage = t('auth.invalid_credentials') || 'Invalid username or password';
      } else {
        errorMessage = t('auth.login_error') || 'Login failed. Please try again.';
      }

      // Show alert for debugging
      alert('Login Error: ' + errorMessage);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page-wrapper">
      <div className="auth-card">
        <h2 className="auth-title">{t('auth.login') || 'লগইন'}</h2>

        {error && (
          <div className="auth-error">
            <i className="fas fa-exclamation-circle"></i>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="username">
              {t('auth.username') || 'ইউজারনেম'}
            </label>
            <div className="input-wrapper">
              <i className="fas fa-user-circle input-icon"></i>
              <input
                type="text"
                id="username"
                name="username"
                className="input-field"
                value={formData.username}
                onChange={handleChange}
                required
                autoComplete="username"
                disabled={loading}
                placeholder={t('auth.username_placeholder') || 'Enter your username'}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="password">
              {t('auth.password') || 'পাসওয়ার্ড'}
            </label>
            <div className="input-wrapper">
              <i className="fas fa-lock input-icon"></i>
              <input
                type="password"
                id="password"
                name="password"
                className="input-field"
                value={formData.password}
                onChange={handleChange}
                required
                autoComplete="current-password"
                disabled={loading}
                placeholder="••••••••"
              />
            </div>
          </div>

          <button
            type="submit"
            className="auth-button"
            disabled={loading}
          >
            {loading ? (t('auth.logging_in') || 'লগইন হচ্ছে...') : (t('auth.login') || 'লগইন')}
          </button>
        </form>

        <div className="auth-footer">
          {t('auth.no_account') || 'অ্যাকাউন্ট নেই?'}
          <Link to="/register" className="auth-link">
            {t('auth.register') || 'নিবন্ধন করুন'}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Login;
